import videojs from "video.js";
import "video.js/dist/video-js.min.css";
import "videojs-overlay/dist/videojs-overlay.css";
import "videojs-upnext/dist/videojs-upnext.css";
// import EventLogger from "./Plugins/EventLogger";

import { useEffect, useState, useRef, useMemo } from "react";
import { VIDEO_CDN } from "@/config/constants";
import { isEncryptedVideo } from "@/helpers";
import { getAdFreeWidgetData, getInterceptorActions } from "@/helpers/api/service";
import { generateDrmLicense, getVideoOverlay, getVideoResource, updatePromoVV, updateVV, getDownloadAppOverlay } from "@/helpers/api/service/Video";
import { getUUID } from "@/utils/auth";
import { cleanOcrText } from "@/utils";
import { useRouter } from "next/router";
import { getOverlayOptions, getOverlayButtonOptions } from "@/helpers/Overlays/VideoPlayer";
import TapToUnmuteButton from "./TapToUnmuteButton";
import VideoElement from "./VideoElement";
import { PAGE, TARGET_GROUP_TYPES, VIEW_FROM } from "@/config/enums";
import { debouncedUpdateVV, checkETData, debouncedUpdatePromoVV } from "@/helpers/VideoPlayer";
import { IVideoPlayer } from "@/helpers/VideoPlayer/interface";
import { toast } from "react-toastify";
import { BrowserView, isDesktop, isIOS, isMobile } from "react-device-detect";
import SkipButton from "./SkipButton";
import PromoCompanion from "./PromoCompanion";
import { IVideoPromo } from "./interfaces";
import { useDispatch, useSelector } from "react-redux";
import { selectPromoCTAClicked, setPromoCompanion, setPromoVideoProgress } from "@/store/slices/promoCompanion";

import "./videoplayer.css";
import { DownloadIcons } from "../DownloadApp/DownloadAppV2";
import { selectStudentId, selectUser } from "@/store/slices/auth";
import { selectOffCanvasAllActionsCompleted, updateOffCanvasView } from "@/store/slices/offCanvas";

const VideoPlayer = (props: IVideoPlayer) => {
  const { videoData, videoTitle, nextVideo, previousVideo, replayVideo, page, skipOverlays, cb } = props;
  const [playerMuted, setPlayerMuted] = useState(!!props.autoplay);
  const [drmLicenseUrl, setDrmLicenseUrl] = useState<string | null>(null);
  const [overlayClicked, setOverlayClicked] = useState<number>(-1);
  const [error, setError] = useState<string>();

  const [downloadBanner, setDownloadBanner] = useState<boolean>(false);

  const et = useRef(0);
  const etOffset = useRef(0);
  const videoTime = useRef(0);
  const prevTimeUpdate = useRef(0);
  const viewId = useRef<number | null>(null);
  const promoViewId = useRef<number | null>(null);
  const resource = useRef<string>(videoData.video_name);
  const mediaType = useRef<string>(videoData.media_type || "video/mp4");
  const isExternalVideo = useRef<boolean>(mediaType.current === "video/youtube");
  const promo = useRef<IVideoPromo | null>();

  const userClicked = useRef<boolean>(false);
  const isBlocked = useRef<{ status: boolean, delay: number, count: number, isPremium: boolean }>({ status: false, delay: 0, count: 0, isPremium: false });
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoMetadataLoadedRef = useRef(false);
  const pipLoadedOnceRef = useRef(false);
  const [player, setPlayer] = useState<any>(null);
  const [isPromo, setIsPromo] = useState<boolean>(false);
  const [promoSkipTime, setPromoSkipTime] = useState<number>(0);
  const router = useRouter();
  const udid = getUUID();
  const viewRefStudentId = props.query.sid ? parseInt(props.query.sid, 13).toString() : 0;
  const viewParentId = props.query.qid ? parseInt(props.query.qid, 13).toString() : 0;

  const nextUrl = nextVideo?.canonicalUrlRelative || undefined;
  const previousUrl = previousVideo?.canonicalUrlRelative || undefined;

  const user = useSelector(selectUser);
  const studentId = useSelector(selectStudentId);
  const updateETCall = checkETData(videoData);

  const allActionsCompleted = useSelector(selectOffCanvasAllActionsCompleted);

  const dispatch = useDispatch();
  const promoCTAClicked = useSelector(selectPromoCTAClicked);

  const unmuteVideo = () => {
    setPlayerMuted(false);
    player?.muted(false);
  }

  const pipListener = () => {
    if (document.pictureInPictureEnabled && !(videoRef.current?.disablePictureInPicture)) {
      try {
        const rect = videoRef.current?.getBoundingClientRect();
        const enablePIP = ((rect?.top ?? 0) + (rect?.height ?? 0)) < 0;
        if (enablePIP && videoMetadataLoadedRef.current) {
          if (!pipLoadedOnceRef.current) {
            videoRef.current?.requestPictureInPicture()
              .then(() => pipLoadedOnceRef.current = true)
              .catch((e) => { });
          }
        } else if (document.pictureInPictureElement) {
          document.exitPictureInPicture();
        }
      } catch (e) { }
    }
  }

  const resumePlaying = () => {
    player.play()
      .then(() => {
        if (!player.muted()) {
          setPlayerMuted(false);
        }
      }).catch((err: any) => {
        player.muted(true);
        player.play();
      });
  }

  const playPlayer = (play?: boolean) => {
    const shouldPlay = play || props.autoplay;
    if (!player || !shouldPlay) {
      return;
    }
    resumePlaying();
  }

  const createVideoOverlay = async (playerRef: any) => {
    if (skipOverlays) {
      return;
    }
    const { data } = await getVideoOverlay(videoData.question_id as number, studentId, videoRef.current?.duration);
    if (!data?.overlays) {
      return;
    }
    data?.overlays.forEach((overlay) => {
      overlay.state = overlayClicked;
      overlay.setState = setOverlayClicked;
      overlay.playerRef = playerRef;
    });

    // @ts-ignore
    import("videojs-overlay").then(() => {
      playerRef.overlay(getOverlayOptions(videoRef.current?.duration, data?.overlays));
    });
  }

  const initPlaybackPlugins = async () => {
    switch (mediaType.current) {
      case "video/youtube":
        // @ts-ignore
        await import("videojs-youtube");
        break;
      case "application/dash+xml":
        await Promise.all([
          // @ts-ignore
          // import("videojs-contrib-dash"),
          // @ts-ignore
          drmLicenseUrl ? import("videojs-contrib-eme") : Promise.resolve(),
        ]);
        break;
      case "application/x-mpegURL":
        await Promise.all([
          // @ts-ignore
          // import("videojs-contrib-hls"),
          // @ts-ignore
          drmLicenseUrl ? import("videojs-contrib-eme") : Promise.resolve(),
        ]);
        break;
      default:
        break;
    }


  }

  const getVideoSrcObj = (videoName: string, mediaType: string) => {
    const prefix = (drmLicenseUrl?.includes("gumlet") && !videoName.startsWith("WV")) ? "/WV" : "";

    return {
      src: videoName.startsWith("http") ? videoName : `${VIDEO_CDN}${prefix}/${videoName}`,
      type: mediaType,
      keySystems: drmLicenseUrl ? {
        "com.widevine.alpha": {
          url: drmLicenseUrl,
          getCertificate: drmLicenseUrl.includes("gumlet") ? async function (_emeOptions: any, callback: Function) {
            let cert_response = await fetch("https://widevine.gumlet.com/certificate/63500125b24c4624148cc58d");
            let widevine_cert = await cert_response.arrayBuffer();
            callback(null, new Uint8Array(widevine_cert))
          } : undefined,
        },
      } : undefined
    };
  }

  const initializePlayer = async (videoName: string, isPromoVideo: boolean) => {
    setIsPromo(isPromoVideo);
    if (!isPromoVideo) {
      player.src(getVideoSrcObj(videoName, mediaType.current));
      resetET();
      setPromoSkipTime(0);
      dispatch(setPromoCompanion(null));
      resumePlaying();
      return;
    }
    if (videoData?.media_type == "DASH" || videoName?.endsWith(".mpd")) {
      mediaType.current = "application/dash+xml";
    } else if (videoData?.media_type == "HLS" || videoName?.endsWith(".m3u8") || videoName?.endsWith(".m3u")) {
      mediaType.current = "application/x-mpegURL";
    }

    const videoOptions = {
      controls: false,
      sources: [getVideoSrcObj(videoName, "video/mp4")],
      playbackRates: [0.25, 0.5, 1, 1.5, 2],
      nativeControlsForTouch: false,
      muted: false,
      autoplay: props.autoplay || false,
      aspectRatio: "16:9",
      poster: props.poster,
      youtube: {
        ytControls: 1,
      },
    };

    if (!videoRef.current) return;
    await initPlaybackPlugins();
    const vp = videojs(videoRef.current, videoOptions);
    setPlayer(vp);

    if (isPromo) return;
    videoRef.current.onloadedmetadata = () => {
      videoMetadataLoadedRef.current = true;
      createVideoOverlay(vp);
    }
  }

  const updateVVDebounced = useMemo(debouncedUpdateVV, []);
  const updatePromoVVDebounced = useMemo(debouncedUpdatePromoVV, []);

  const resetET = () => {
    et.current = 0;
    etOffset.current = 0;
    videoTime.current = 0;
    prevTimeUpdate.current = 0;
  }

  const replayPlayer = () => {
    if (nextUrl) {
      return;
    }
    viewId.current = null;
    resetET();
    playPlayer();
  }

  const initComponent = () => {
    if (updateETCall) updateVV({
      view_id: viewId.current,
      answer_id: videoData?.answer_id,
      answer_video: resource.current,
      udid,
      refStudentId: viewRefStudentId,
      parent_id: viewParentId,
      engage_time: 0,
      video_time: 0,
      question_id: videoData?.question_id,
      view_from: router.query.viewFrom ?? VIEW_FROM.DIRECT,
      source: props.query.source ?? "mweb",
      webStudentId: studentId,
    }).then((res) => {
      if (res.data?.view_id) {
        viewId.current = res.data.view_id;
      }
    });
    window.addEventListener("scroll", pipListener);

    if (!(isEncryptedVideo(resource.current) && !drmLicenseUrl && videoData?.question_id)) {
      initializePlayer(promo.current?.intro.video.resource || "", true);
      return;
    }
    generateDrmLicense(videoData.question_id as number).then((res) => {
      if ((res.data as { code: number; message: string }).code === 500) {
        toast.error((res.data as { code: number; message: string }).message);
        return;
      }
      setDrmLicenseUrl((res.data as { licenseUrl: string }).licenseUrl);
    });
  };

  const initProPlugin = async () => {
    if (!isBlocked.current.status || isExternalVideo.current) return;

    const [{ data }] = await Promise.all([
      getAdFreeWidgetData("VIDEO_OVERLAY"),
      import("./Plugins/Pro"),
    ]);
    player.pro(user.id, router.asPath, data?.widgets?.[0]?.items);
  }

  const getET = () => {
    const currentTimeUpdate = player.currentTime();
    const timeDiff = Math.abs(currentTimeUpdate - (prevTimeUpdate.current || 0));
    const totalET = (et.current || 0) + (etOffset.current || 0);

    const etTime = (et.current || 0) + (timeDiff / player.playbackRate());
    if (timeDiff < 2) {
      et.current = etTime;
    } else {
      et.current = 0;
      etOffset.current = totalET;
    }
    prevTimeUpdate.current = currentTimeUpdate;
    if (currentTimeUpdate > (videoTime.current || 0)) {
      videoTime.current = currentTimeUpdate;
    }
    return totalET;
  }

  useEffect(() => {
    if (!props.fetchResource) {
      initComponent();
    } else {
      getVideoResource(
        videoData.question_id as string,
        videoData.answer_id as string,
        studentId,
        page)
        .then(({ data }: { data: any }) => {
          isBlocked.current = { ...data.isBlocked, count: 0 };
          resource.current = data.resource || videoData.video_name;
          promo.current = data.promo;
        }).catch().then(() => {
          initComponent();
        });
    }

    return () => {
      if (player) {
        player.dispose();
      }
      window.removeEventListener("scroll", pipListener);
      updatePromoVVDebounced.cancel();
      updateVVDebounced.cancel();
    }
  }, []);

  useEffect(() => {
    if (!player) return;
    player.isPromo = isPromo;
    if (isPromo) return;

    player.ready(() => {
      player.on("playing", (e: any) => {
        player.removeClass("vjs-custom-waiting");
        player.controls(true);
        player.isPlaying = true;
        const time = Math.floor(player.currentTime());
        if (time > 10) {
          player.trigger("playing>10");
        }

        if (cb?.["PLAYER_playing"]) {
          cb["PLAYER_playing"](e);
        }
      });

      player.on("timeupdate", () => {
        const currentTimeUpdate = player.currentTime();
        const totalET = getET();

        const totalETInt = Math.ceil(totalET);
        if (totalETInt && totalETInt % 10 == 0) {
          if (updateETCall) updateVVDebounced({
            view_id: viewId.current,
            answer_id: videoData.answer_id,
            answer_video: resource.current,
            udid,
            refStudentId: viewRefStudentId,
            parent_id: viewParentId,
            engage_time: totalETInt,
            video_time: Math.ceil(videoTime.current || 0),
            question_id: videoData.question_id,
            view_from: router.query.viewFrom ?? VIEW_FROM.DIRECT,
            source: props.query.source ?? "mweb",
            webStudentId: studentId,

          }).then((res: any) => {
            if (res.data?.view_id) {
              viewId.current = res.data.view_id;
            }
          });
        }
        if (isBlocked.current.status && (currentTimeUpdate > isBlocked.current.delay) && !user.vipData?.pro.enabled && mediaType.current === "video/mp4") {
          isBlocked.current.count++;
          player.trigger("show-pro-overlay", isBlocked.current.count);
          if (updateETCall) updateVVDebounced({
            view_id: viewId.current,
            answer_id: videoData.answer_id,
            answer_video: resource.current,
            udid,
            refStudentId: viewRefStudentId,
            parent_id: viewParentId,
            engage_time: totalETInt,
            video_time: Math.ceil(videoTime.current || 0),
            question_id: videoData.question_id,
            view_from: VIEW_FROM.BLOCKED,
            source: props.query.source ?? "mweb",
            webStudentId: studentId,
          });
        }
      });

      player.on("ended", () => {
        const totalET = (et.current || 0) + (etOffset.current || 0);
        const totalETInt = Math.ceil(totalET);

        if (updateETCall) updateVV({
          view_id: viewId.current,
          answer_id: videoData.answer_id,
          answer_video: resource.current,
          udid,
          refStudentId: viewRefStudentId,
          parent_id: viewParentId,
          engage_time: totalETInt,
          video_time: Math.ceil(videoTime.current || 0),
          question_id: videoData.question_id,
          view_from: router.query.viewFrom ?? VIEW_FROM.DIRECT,
          source: props.query.source ?? "mweb",
          webStudentId: studentId,
        });

        if (replayVideo) replayPlayer();
      });

      initProPlugin();

      if (isDesktop) {
        import("videojs-hotkeys").then(() => {
          player.hotkeys({
            volumeStep: 0.1,
            seekStep: 10,
            enableModifiersForNumbers: false,
          });
        });
      }

      getDownloadAppOverlay(Number(videoData.question_id), Number(studentId)).then((res) => {
        const showDownloadApp = res.data.href && res.data.title && res.data.bottomTitle;
        console.log("FSDsfds", res, showDownloadApp);
        if (showDownloadApp) {
          import("./Plugins/DownloadApp").then(() => {
            player.on("ended", () => {
              player.downloadApp({
                ...res.data
              });
            });
          });
          return;
        }
        if (nextUrl) {
          import("./Plugins/Upnext").then(() => {
            player.upnext({
              timeout: 5000,
              headText: "Up Next",
              cancelText: "Cancel",
              getTitle: () => cleanOcrText(nextVideo?.ocr_text),
              next: () => { router.push(`${nextUrl}`) }
            });
          });
        }
      });


      if (!props.isInModal && !isExternalVideo.current) {
        import("./Plugins/OverlayButtons").then(() => {
          player.touchOverlay(getOverlayButtonOptions(player, resumePlaying, router, { nextUrl, previousUrl }));
        });
      }
    });
  }, [player, isPromo]);

  useEffect(() => {
    if (!drmLicenseUrl) {
      return;
    }
    initializePlayer(resource.current, true);
  }, [drmLicenseUrl]);

  useEffect(() => {
    if (!player) {
      return;
    }
    // new EventLogger(player);

    player.ready(async () => {
      // tracking play event
      player.bigPlayButton.on(isMobile ? "tap" : "click", () => {
        window.dataLayer?.push({
          event: "video-playclick"
        });
      });

      if (props.autoplay) {
        player.addClass("vjs-custom-waiting");
      }

      player.on("promo-skip", () => {
        if (!player.isPromo) return;

        const totalET = getET();
        const totalETInt = Math.ceil(totalET);
        updatePromoVV({
          viewId: promoViewId.current,
          promoId: promo.current?.intro.id,
          questionId: videoData.question_id,
          et: totalETInt,
          udid,
          webStudentId: studentId,
          isSkipped: true,
        });
        initializePlayer(resource.current, false);
      });

      player.on("ended", () => {
        if (!player.isPromo) return;
        const totalET = (et.current || 0) + (etOffset.current || 0);
        const totalETInt = Math.ceil(totalET);

        updatePromoVV({
          viewId: promoViewId.current,
          promoId: promo.current?.intro.id,
          questionId: videoData.question_id,
          et: totalETInt,
          udid,
          webStudentId: studentId,
        });
        initializePlayer(resource.current, false);
      });

      player.on("error", () => {
        if (player.isPromo) {
          initializePlayer(resource.current, false);
          return;
        }
        player.removeClass("vjs-custom-waiting");
        player.removeClass("vjs-waiting");
        setError(player.error().message);
        if (resource.current?.endsWith(".mpd") || resource.current?.endsWith(".m3u8") || resource.current?.endsWith(".m3u")) {
          setDownloadBanner(true);
        }
      });

      player.on("volumechange", () => {
        if (!player.muted()) {
          setPlayerMuted(false);
        }
      });

      player.on("playing", (e: any) => {
        player.isPlaying = true;
        player.removeClass("vjs-custom-waiting");
        if (player.isPromo) {
          setPromoSkipTime(promo.current?.intro.video.skipTime || 0);
          dispatch(setPromoCompanion(promo.current?.intro.companionStrip));
        }
      });

      player.on("pause", (e: any) => {
        player.isPlaying = false;
        const time = player.currentTime();
        if (time > 10) {
          player.trigger("pause>10");
        }
      });

      player.on("timeupdate", () => {
        const progress = Math.floor((player.currentTime() / player.duration()) * 100);
        dispatch(setPromoVideoProgress(progress));

        if (!player.isPromo) return;
        const totalET = getET();

        const totalETInt = Math.ceil(totalET);
        if (totalETInt % 5 == 0) {
          updatePromoVVDebounced({
            viewId: promoViewId.current,
            promoId: promo.current?.intro.id,
            questionId: videoData.question_id,
            et: totalETInt,
            udid,
            webStudentId: studentId,
          }).then((res: any) => {
            if (res.data?.viewId) {
              promoViewId.current = res.data.viewId;
            }
          });
        }
      });

      try {
        const response = await getInterceptorActions(PAGE.VIDEO_PLAYER, { studentId, questionId: (videoData.question_id as Number).toString() });
        const actions = response.data?.modal_list;
        if (!actions.length) return;
        player.bigPlayButton.off("click");
        player.bigPlayButton.off("tap");
        player.bigPlayButton.on(isMobile ? "tap" : "click", () => {
          window.dataLayer?.push({
            event: "video-playclick"
          });
          dispatch(updateOffCanvasView(actions));
        });
      } catch { }
    });

    player.eme?.();

    if (isMobile) {
      // @ts-ignore
      import("videojs-landscape-fullscreen").then(() => {
        player.landscapeFullscreen({
          fullscreen: {
            enterOnRotate: true,
            alwaysInLandscapeMode: true,
            iOS: true,
          },
        });
      });
    }

    if (props.alternateVideoLanguages?.length) {
      import("./Plugins/LanguageSwitcher").then(() => {
        player.langChangeBtn(props.alternateVideoLanguages, router);
      });
    }

    playPlayer();
    return () => {
      if (player) {
        player.dispose();
      }
    }
  }, [player]);

  useEffect(() => {
    if (!allActionsCompleted) {
      return;
    }
    player.play();
  }, [allActionsCompleted]);

  useEffect(() => {
    if (!promoCTAClicked) return;
    const totalET = getET();
    const totalETInt = Math.ceil(totalET);
    updatePromoVV({
      viewId: promoViewId.current,
      promoId: promo.current?.intro.id,
      questionId: videoData.question_id,
      et: totalETInt,
      udid,
      webStudentId: studentId,
      ctaClicked: true,
    });
  }, [promoCTAClicked]);

  return (
    <>
      <VideoElement videoTitle={videoTitle} videoRef={videoRef} className={props.autoplay ? "vjs-autoplay" : ""} />
      {error &&
        <p className="absolute-center vjs-error z-50 bg-gray-600 h-full w-full p-4 text-white text-center flex justify-center items-center">
          {downloadBanner ?
            <DownloadIcons promoText="Please Download App to view Content" />
            :
            error
          }
        </p>}
      <SkipButton player={player} time={promoSkipTime} />
      {
        promo.current?.intro.companionStrip &&
        <BrowserView>
          <PromoCompanion />
        </BrowserView>
      }
      {(userClicked.current || props.autoplay) && <TapToUnmuteButton playerMuted={playerMuted} unmuteVideo={unmuteVideo} />}
    </>
  );
}

export default VideoPlayer;
